import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { IBgField, IGetColorFromValue, IToggleButtons } from '../types';
import Typography from '../../../components/Typography';
import { ReactComponent as ArrowUpV2 } from '../../../assets/icons/arrow-up-v2.svg';
import { ReactComponent as ArrowDownV2 } from '../../../assets/icons/arrow-down-v2.svg';
import { ReactComponent as TableFilterIcon } from '../../../assets/icons/table-filter-icon.svg';

const useStyles = makeStyles(() => ({
  border: {
    border: `1px solid #D3D3D3`,
  },

  table: {
    border: `1px solid #D3D3D3`,
  },

  borderRight: {
    borderLeft: `1px solid #D3D3D3`,
    borderBottom: `1px solid #D3D3D3`,
  },

  borderLeft: {
    borderLeft: `1px solid #D3D3D3`,
    borderBottom: `1px solid #D3D3D3`,
  },
}));

export const formatValue = (value: number | null, field: string) => {
  if (field === 'return1yrWs' && value) return `${Number(Number(value) * 100).toFixed(1)}%`;
  if (field === 'ukPriceMin' && value) return Number(value).toFixed(0);
  if (field === 'wsPrice' && value) return Number(value).toFixed(0);

  return value;
};

export const ToggleButtons = ({ onclick, selected, ukPriceMin, wsPrice, tradeCount }: IToggleButtons): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation();
  const white = [255, 255, 255];
  const red = [214, 69, 80];
  const orange = [217, 179, 0];
  const lightBlue = [222, 239, 255];
  const lightPink = [239, 181, 185];

  const data: IBgField[] = [
    { title: t('common:wsGrade'), value: 'wsGrade', minimum: 0, maximum: 10 },
    {
      title: t('common:liquidity'),
      value: 'liquidity',
      minimum: 0,
      maximum: 5,
      colorsConfig: { minColor: lightBlue, maxColor: red },
    },
    {
      title: t('common:tradeCount'),
      value: 'tradeCount',
      minimum: tradeCount.min,
      center: tradeCount.min,
      maximum: tradeCount.max,
      colorsConfig: { minColor: white, maxColor: red }, // l/h
    },
    {
      title: t('common:score'),
      value: 'score',
      minimum: 80,
      center: 90,
      maximum: 100,
      colorsConfig: { minColor: lightBlue, centerColor: orange, maxColor: red },
      customColorsFunc: (value) => {
        let config = { bgColor: '#0f7fe5', textColor: '' };

        if (value >= 100) {
          config = { bgColor: '#c03e48', textColor: 'white' };
        } else if (value >= 97) {
          config = { bgColor: '#c15239', textColor: 'white' };
        } else if (value >= 96) {
          config = { bgColor: '#c1652b', textColor: 'white' };
        } else if (value >= 93) {
          config = { bgColor: '#c28316', textColor: 'white' };
        } else if (value >= 90) {
          config = { bgColor: '#c3a100', textColor: 'white' };
        } else if (value >= 85) {
          config = { bgColor: '#7b935c', textColor: 'white' };
        } else if (value >= 80) {
          config = { bgColor: '#0f7fe5', textColor: 'white' };
        }

        return config;
      },
    },
    { title: t('common:return1yr'), value: 'return1yrWs', minimum: -2, center: 0, maximum: 2 },
    { title: t('common:compGrade'), value: 'compGrade', minimum: 0, maximum: 10 },
    {
      title: t('common:wsPrice'),
      value: 'wsPrice',
      minimum: wsPrice.min,
      maximum: wsPrice.max,
      colorsConfig: { minColor: lightBlue, maxColor: lightPink }, // l/h
    },
    {
      title: t('common:ukPriceMin'),
      value: 'ukPriceMin',
      minimum: ukPriceMin.min,
      maximum: ukPriceMin.max,
      colorsConfig: { minColor: lightBlue, maxColor: lightPink }, // l/h
    },
  ];

  return (
    <div className="tw-flex tw-flex-row tw-gap-4 tw-flex-wrap">
      {data.map((x, index) => {
        return (
          <button
            key={index}
            className={`tw-p-2 tw-text-center tw-text-sm tw-outline-none ${classes.table} ${
              selected === x.value ? 'tw-bg-red-200' : 'tw-bg-white'
            }`}
            type="button"
            onClick={() => {
              onclick(x);
            }}
          >
            <Typography>{x.title} </Typography>
          </button>
        );
      })}
    </div>
  );
};

export const getColorFromValue = ({
  value,
  maximum,
  minimum,
  center,
  colorsConfig,
  customColorsFunc,
}: IGetColorFromValue) => {
  const interpolateColor = (color1: number[], color2: number[], ratio: number) => {
    return color1.map((c, i) => Math.round(c + (color2[i] - c) * ratio)) as number[];
  };

  if (value === null || value === undefined) return { bgColor: 'rgba(255, 255, 255, 1)', textColor: '' };

  if (customColorsFunc) return customColorsFunc(value);

  const actualCenter = center ?? (minimum + maximum) / 2;

  const clampedValue = Math.max(minimum, Math.min(maximum, value));

  const blue: number[] = colorsConfig?.minColor ?? [17, 141, 255];
  const white: number[] = colorsConfig?.centerColor ?? [255, 255, 255];
  const red: number[] = colorsConfig?.centerColor ?? [214, 69, 80];

  let ratio: number;
  let color: number[];

  if (clampedValue <= actualCenter) {
    ratio = (clampedValue - minimum) / (actualCenter - minimum || 1);
    color = interpolateColor(blue, white, ratio);
  } else {
    ratio = (clampedValue - actualCenter) / (maximum - actualCenter || 1);
    color = interpolateColor(white, red, ratio);
  }

  const luminance = 0.2126 * color[0] + 0.7152 * color[1] + 0.0722 * color[2];

  return { bgColor: `rgba(${color[0]}, ${color[1]}, ${color[2]}, 1)`, textColor: luminance < 128 ? 'white' : '' };
};

export const sortDirectionArrow = (sortDirection: string, key?: string) => {
  if (!key) {
    return <TableFilterIcon fill="#8A809A" className="tw-w-3 tw-h-3 tw-min-w-[14px] tw-min-h-[14px]" />;
  }

  return sortDirection === 'asc' ? (
    <ArrowUpV2 fill="#8A809A" className="tw-w-3 tw-h-3 tw-min-w-[14px] tw-min-h-[14px]" />
  ) : (
    <ArrowDownV2 fill="#8A809A" className="tw-w-3 tw-h-3 tw-min-w-[14px] tw-min-h-[14px]" />
  );
};
